import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, ImagePlus, Images, Loader2, Package, Plus, QrCode, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";
import ProductGalleryEditor from "@/components/admin/ProductGalleryEditor";
import RichTextEditor from "@/components/admin/RichTextEditor";

const EMPTY = {
  name: "",
  category: "miels",
  price: "",
  stock: 10,
  description: "",
  details: "",
  weight: "",
  shipping_weight: 250,
  badge: "",
  origin: "",
  harvest: "",
  notes: "",
  intensity: 3,
  featured: false,
  available: true,
  bookable: false,
  image_url: "",
  images: [],
  related_products: [],
  related_categories: [],
  slug_aliases: [],
  is_box: false,
  box_options: [],
  box_tiers: [],
  subscription: { enabled: false, honeys_per_box: 4, monthly_price: "", bimonthly_price: "" },
};

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function AdminProductForm() {
  const { productId } = useParams();
  const isEdit = Boolean(productId);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [boxImgPickerIdx, setBoxImgPickerIdx] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [qrLoading, setQrLoading] = useState(false);

  const generateQr = async () => {
    setQrLoading(true);
    try {
      const { data } = await api.get(`/admin/products/${productId}/qrcode`, { params: { origin: window.location.origin }, responseType: "blob" });
      setQrUrl(window.URL.createObjectURL(data));
    } catch {
      toast.error("Génération du QR Code impossible");
    } finally {
      setQrLoading(false);
    }
  };

  const downloadQr = () => {
    if (!qrUrl) return;
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `qrcode_${productId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.get("/categories").then(({ data }) => {
      setCategories(data);
      if (!isEdit && data.length > 0) {
        setForm((f) => (data.some((c) => c.category_id === f.category) ? f : { ...f, category: data[0].category_id }));
      }
    }).catch(() => {});
    api.get("/products").then(({ data }) => setAllProducts(data)).catch(() => {});
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/products/${productId}`)
      .then(({ data }) => {
        setForm({
          name: data.name || "",
          category: data.category || "miels",
          price: ((data.price_cents || 0) / 100).toFixed(2),
          stock: data.stock ?? 0,
          description: data.description || "",
          details: data.details || "",
          weight: data.weight || "",
          shipping_weight: data.shipping_weight ?? 250,
          badge: data.badge || "",
          origin: data.origin || "",
          harvest: data.harvest || "",
          notes: (data.notes || []).join(", "),
          intensity: data.intensity || 3,
          featured: Boolean(data.featured),
          available: data.available !== false,
          bookable: Boolean(data.bookable),
          image_url: data.image_url || "",
          images: data.images || [],
          related_products: data.related_products || [],
          related_categories: data.related_categories || [],
          slug_aliases: data.slug_aliases || [],
          is_box: Boolean(data.is_box),
          box_options: (data.box_options || []).map((o) => (typeof o === "string" ? { name: o, image_url: "" } : { name: o.name || "", image_url: o.image_url || "" })),
          box_tiers: (data.box_tiers || []).map((t) => ({ count: t.count, price: ((t.price_cents || 0) / 100).toFixed(2) })),
          subscription: {
            enabled: Boolean(data.subscription?.enabled),
            honeys_per_box: data.subscription?.honeys_per_box || 4,
            monthly_price: data.subscription?.monthly_price_cents ? (data.subscription.monthly_price_cents / 100).toFixed(2) : "",
            bimonthly_price: data.subscription?.bimonthly_price_cents ? (data.subscription.bimonthly_price_cents / 100).toFixed(2) : "",
          },
        });
      })
      .catch(() => toast.error("Produit introuvable"))
      .finally(() => setLoading(false));
  }, [productId, isEdit]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const toggleArrayItem = (key, id) => setForm((f) => {
    const list = f[key] || [];
    return { ...f, [key]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
  });

  const addBoxOption = () => setForm((f) => ({ ...f, box_options: [...(f.box_options || []), { name: "", image_url: "" }] }));
  const updateBoxOption = (idx, key, value) => setForm((f) => ({ ...f, box_options: f.box_options.map((o, i) => (i === idx ? { ...o, [key]: value } : o)) }));
  const removeBoxOption = (idx) => setForm((f) => ({ ...f, box_options: f.box_options.filter((_, i) => i !== idx) }));
  const addBoxTier = () => setForm((f) => ({ ...f, box_tiers: [...(f.box_tiers || []), { count: 4, price: "" }] }));
  const updateBoxTier = (idx, key, value) => setForm((f) => ({ ...f, box_tiers: f.box_tiers.map((t, i) => (i === idx ? { ...t, [key]: value } : t)) }));
  const removeBoxTier = (idx) => setForm((f) => ({ ...f, box_tiers: f.box_tiers.filter((_, i) => i !== idx) }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanOptions = (form.box_options || [])
      .map((o) => ({ name: (o.name || "").trim(), image_url: (o.image_url || "").trim() }))
      .filter((o) => o.name);
    const cleanTiers = (form.box_tiers || [])
      .map((t) => ({ count: parseInt(t.count, 10) || 0, price: parseFloat(String(t.price).replace(",", ".")) || 0 }))
      .filter((t) => t.count > 0 && t.price > 0);
    const sub = form.subscription || {};
    const subMonthly = parseFloat(String(sub.monthly_price).replace(",", ".")) || 0;
    const subBimonthly = parseFloat(String(sub.bimonthly_price).replace(",", ".")) || 0;
    if (form.is_box) {
      if (cleanOptions.length === 0) {
        toast.error("Ajoutez au moins un miel disponible pour le coffret");
        return;
      }
      if (cleanTiers.length === 0) {
        toast.error("Ajoutez au moins un palier (nombre de miels + prix)");
        return;
      }
      if (new Set(cleanTiers.map((t) => t.count)).size !== cleanTiers.length) {
        toast.error("Deux paliers ne peuvent pas avoir le même nombre de miels");
        return;
      }
      if (sub.enabled && subMonthly <= 0 && subBimonthly <= 0) {
        toast.error("Renseignez au moins un tarif d'abonnement (mensuel ou bimestriel)");
        return;
      }
    }
    setSaving(true);
    const priceValue = form.is_box
      ? Math.min(...cleanTiers.map((t) => t.price))
      : parseFloat(String(form.price).replace(",", "."));
    const payload = {
      ...form,
      price: priceValue,
      stock: parseInt(form.stock, 10) || 0,
      shipping_weight: parseInt(form.shipping_weight, 10) || 0,
      intensity: parseInt(form.intensity, 10) || 3,
      notes: form.notes.split(",").map((n) => n.trim()).filter(Boolean),
      is_box: form.is_box,
      box_options: form.is_box ? cleanOptions : [],
      box_tiers: form.is_box ? cleanTiers : [],
      subscription: {
        enabled: Boolean(form.is_box && sub.enabled),
        honeys_per_box: parseInt(sub.honeys_per_box, 10) || 4,
        monthly_price: subMonthly,
        bimonthly_price: subBimonthly,
      },
    };
    try {
      if (isEdit) {
        await api.put(`/admin/products/${productId}`, payload);
        toast.success("Produit mis à jour — la boutique est déjà à jour");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Produit créé — il est visible dans la boutique");
      }
      navigate("/admin/produits");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p data-testid="product-form-loading" className="py-20 text-center font-serif text-xl text-bark-soft">Chargement…</p>;
  }

  return (
    <div data-testid="admin-product-form" className="space-y-6">
      <div>
        <Link to="/admin/produits" data-testid="form-back" className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-bark-soft hover:text-amber-deep">
          <ArrowLeft size={15} /> Retour aux produits
        </Link>
        <h1 className="font-serif text-3xl font-semibold text-bark">{isEdit ? "Modifier le produit" : "Nouveau produit"}</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm lg:col-span-2">
          <div>
            <label htmlFor="pf-name" className="mb-1.5 block text-sm font-medium text-bark">Nom *</label>
            <input id="pf-name" data-testid="pf-name" required value={form.name} onChange={set("name")} className={inputClass} placeholder="Miel de Tilleul du Luberon" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label htmlFor="pf-category" className="mb-1.5 block text-sm font-medium text-bark">Catégorie *</label>
              <select id="pf-category" data-testid="pf-category" value={form.category} onChange={set("category")} className={inputClass}>
                {categories.map((c) => (
                  <option key={c.category_id} value={c.category_id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pf-price" className="mb-1.5 block text-sm font-medium text-bark">Prix (€) {form.is_box ? "" : "*"}</label>
              <input id="pf-price" data-testid="pf-price" required={!form.is_box} disabled={form.is_box} type="number" min="0.01" step="0.01" value={form.is_box ? "" : form.price} onChange={set("price")} className={`${inputClass} ${form.is_box ? "cursor-not-allowed opacity-60" : ""}`} placeholder={form.is_box ? "Défini par les paliers" : "12,90"} />
            </div>
            <div>
              <label htmlFor="pf-stock" className="mb-1.5 block text-sm font-medium text-bark">Stock *</label>
              <input id="pf-stock" data-testid="pf-stock" required type="number" min="0" step="1" value={form.stock} onChange={set("stock")} className={inputClass} />
            </div>
          </div>
          <div>
            <label htmlFor="pf-description" className="mb-1.5 block text-sm font-medium text-bark">Description</label>
            <RichTextEditor id="pf-description" testId="pf-description" rows={6} value={form.description} onChange={(v) => setForm((f) => ({ ...f, description: v }))} className={inputClass} placeholder="Racontez ce produit : terroir, saveurs, savoir-faire…" />
          </div>
          <div>
            <label htmlFor="pf-details" className="mb-1.5 block text-sm font-medium text-bark">Informations complémentaires <span className="font-normal text-bark-soft">(facultatif, affichées dans un bloc à déplier)</span></label>
            <RichTextEditor id="pf-details" testId="pf-details" rows={6} value={form.details} onChange={(v) => setForm((f) => ({ ...f, details: v }))} className={inputClass} placeholder="Composition, conseils de conservation, utilisation, origine des fleurs…" />
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="pf-weight" className="mb-1.5 block text-sm font-medium text-bark">Contenance</label>
              <input id="pf-weight" data-testid="pf-weight" value={form.weight} onChange={set("weight")} className={inputClass} placeholder="250 g" />
            </div>
            <div>
              <label htmlFor="pf-shipping-weight" className="mb-1.5 block text-sm font-medium text-bark">Poids envoi (g)</label>
              <input id="pf-shipping-weight" data-testid="pf-shipping-weight" type="number" min="0" step="1" value={form.shipping_weight} onChange={set("shipping_weight")} className={inputClass} placeholder="350" />
              <p className="mt-1 text-xs text-stone-mute">Jamais affiché aux clients — sert au calcul des frais de port (produit + emballage).</p>
            </div>
            <div>
              <label htmlFor="pf-badge" className="mb-1.5 block text-sm font-medium text-bark">Badge</label>
              <input id="pf-badge" data-testid="pf-badge" value={form.badge} onChange={set("badge")} className={inputClass} placeholder="Récolte 2026, Bio, Rareté…" />
            </div>
            <div>
              <label htmlFor="pf-origin" className="mb-1.5 block text-sm font-medium text-bark">Terroir / origine</label>
              <input id="pf-origin" data-testid="pf-origin" value={form.origin} onChange={set("origin")} className={inputClass} placeholder="Isère, Auvergne-Rhône-Alpes" />
            </div>
            <div>
              <label htmlFor="pf-harvest" className="mb-1.5 block text-sm font-medium text-bark">Récolte</label>
              <input id="pf-harvest" data-testid="pf-harvest" value={form.harvest} onChange={set("harvest")} className={inputClass} placeholder="Juillet 2026" />
            </div>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="pf-notes" className="mb-1.5 block text-sm font-medium text-bark">Notes de dégustation (séparées par des virgules)</label>
              <input id="pf-notes" data-testid="pf-notes" value={form.notes} onChange={set("notes")} className={inputClass} placeholder="Floral, Boisé, Doux" />
            </div>
            <div>
              <label htmlFor="pf-intensity" className="mb-1.5 block text-sm font-medium text-bark">Intensité (1 à 5)</label>
              <select id="pf-intensity" data-testid="pf-intensity" value={form.intensity} onChange={set("intensity")} className={inputClass}>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{n} — {["Très doux", "Doux", "Équilibré", "Corsé", "Puissant"][n - 1]}</option>
                ))}
              </select>
            </div>
          </div>
          <div data-testid="pf-available-block" className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${form.available ? "border-sage/40 bg-sage/5" : "border-terra/30 bg-terra/5"}`}>
            <div>
              <p className="text-sm font-semibold text-bark">Disponible à la vente</p>
              <p className="mt-0.5 text-xs text-bark-soft">
                {form.available
                  ? "Le produit est visible et achetable sur la boutique."
                  : "Le produit est masqué de la boutique (invisible et non achetable côté client)."}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.available}
              data-testid="pf-available-toggle"
              onClick={() => setForm((f) => ({ ...f, available: !f.available }))}
              className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${form.available ? "bg-sage" : "bg-stone-mute/40"}`}
            >
              <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform ${form.available ? "translate-x-[22px]" : "translate-x-0.5"}`} />
            </button>
          </div>
          <label data-testid="pf-featured-label" className="flex cursor-pointer items-center gap-3 text-sm font-medium text-bark">
            <input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-amber-deep" />
            Mettre à la une sur la page d'accueil
          </label>
          <label data-testid="pf-bookable-label" className={`flex items-center gap-3 text-sm font-medium text-bark ${form.is_box ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
            <input data-testid="pf-bookable" type="checkbox" checked={form.bookable} disabled={form.is_box} onChange={set("bookable")} className="h-4 w-4 accent-amber-deep" />
            Produit avec réservation (agenda — ex. visite de ruche)
          </label>
          {form.bookable && (
            <p className="rounded-xl bg-sage-light px-4 py-3 text-xs text-sage">
              Les clients choisiront une date et un horaire sur la fiche. Gérez les créneaux disponibles dans l'onglet « Agenda » de l'admin.
            </p>
          )}

          <label data-testid="pf-isbox-label" className={`flex items-center gap-3 text-sm font-medium text-bark ${form.bookable ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
            <input data-testid="pf-isbox" type="checkbox" checked={form.is_box} disabled={form.bookable} onChange={(e) => setForm((f) => ({ ...f, is_box: e.target.checked }))} className="h-4 w-4 accent-amber-deep" />
            Produit coffret — le client compose sa sélection de miels
          </label>

          {form.is_box && (
            <div data-testid="pf-box-config" className="space-y-5 rounded-xl border border-amber-900/10 bg-cream/60 p-4">
              <div className="flex items-center gap-2 text-amber-deep">
                <Package size={18} />
                <p className="font-serif text-lg font-semibold text-bark">Composition du coffret</p>
              </div>

              <div>
                <p className="text-sm font-medium text-bark">Miels disponibles</p>
                <p className="mb-2 text-xs text-stone-mute">La liste dans laquelle le client fera son choix. Ajoutez, renommez ou supprimez des miels.</p>
                <div className="space-y-2">
                  {(form.box_options || []).length === 0 ? (
                    <p className="text-xs text-stone-mute">Aucun miel — ajoutez-en un ci-dessous.</p>
                  ) : (
                    form.box_options.map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        {opt.image_url ? (
                          <img src={resolveImage(opt.image_url)} alt="" className="h-11 w-11 shrink-0 rounded-lg border border-amber-900/10 object-cover" />
                        ) : (
                          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-dashed border-amber-900/25 bg-white text-stone-mute">
                            <ImagePlus size={16} />
                          </div>
                        )}
                        <input
                          data-testid={`pf-box-option-${idx}`}
                          value={opt.name}
                          onChange={(e) => updateBoxOption(idx, "name", e.target.value)}
                          placeholder="Ex. Lavande"
                          className="flex-1 rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep"
                        />
                        <button type="button" data-testid={`pf-box-option-img-${idx}`} onClick={() => setBoxImgPickerIdx(idx)} className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-3 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep" title="Choisir une photo">
                          <Images size={15} /> Photo
                        </button>
                        <button type="button" data-testid={`pf-box-option-remove-${idx}`} onClick={() => removeBoxOption(idx)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10" aria-label="Supprimer ce miel">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button type="button" data-testid="pf-box-option-add" onClick={addBoxOption} className="mt-2 flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">
                  <Plus size={15} /> Ajouter un miel
                </button>
              </div>

              <div>
                <p className="text-sm font-medium text-bark">Paliers du coffret</p>
                <p className="mb-2 text-xs text-stone-mute">Chaque palier = un nombre de miels à choisir et son prix. Ex. 4 miels → 34,90 €.</p>
                <div className="space-y-2">
                  {(form.box_tiers || []).length === 0 ? (
                    <p className="text-xs text-stone-mute">Aucun palier — ajoutez-en un ci-dessous.</p>
                  ) : (
                    form.box_tiers.map((tier, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <input
                            data-testid={`pf-box-tier-count-${idx}`}
                            type="number" min="1" max="50"
                            value={tier.count}
                            onChange={(e) => updateBoxTier(idx, "count", e.target.value)}
                            className="w-20 rounded-xl border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep"
                          />
                          <span className="text-sm text-bark-soft">miels</span>
                        </div>
                        <span className="text-stone-mute">→</span>
                        <div className="flex flex-1 items-center gap-1.5">
                          <input
                            data-testid={`pf-box-tier-price-${idx}`}
                            type="number" min="0.01" step="0.01"
                            value={tier.price}
                            onChange={(e) => updateBoxTier(idx, "price", e.target.value)}
                            placeholder="34,90"
                            className="w-full rounded-xl border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep"
                          />
                          <span className="text-sm text-bark-soft">€</span>
                        </div>
                        <button type="button" data-testid={`pf-box-tier-remove-${idx}`} onClick={() => removeBoxTier(idx)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-terra/30 text-terra transition-colors hover:bg-terra/10" aria-label="Supprimer ce palier">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button type="button" data-testid="pf-box-tier-add" onClick={addBoxTier} className="mt-2 flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">
                  <Plus size={15} /> Ajouter un palier
                </button>
              </div>

              <div className="border-t border-amber-900/10 pt-4">
                <label data-testid="pf-sub-enabled-label" className="flex cursor-pointer items-center gap-3 text-sm font-medium text-bark">
                  <input data-testid="pf-sub-enabled" type="checkbox" checked={form.subscription.enabled} onChange={(e) => setForm((f) => ({ ...f, subscription: { ...f.subscription, enabled: e.target.checked } }))} className="h-4 w-4 accent-amber-deep" />
                  Proposer en abonnement annuel (cadeau possible)
                </label>
                {form.subscription.enabled && (
                  <div className="mt-3 space-y-3">
                    <p className="text-xs text-stone-mute">Paiement unique pour l'année. Mensuelle = 12 livraisons/an, bimestrielle = 6 livraisons/an. La 1ʳᵉ box suit le choix du client, puis surprises de l'apiculteur.</p>
                    <div className="grid gap-3 sm:grid-cols-3">
                      <div>
                        <label className="mb-1 block text-xs font-medium text-bark">Miels par box</label>
                        <input data-testid="pf-sub-count" type="number" min="1" max="50" value={form.subscription.honeys_per_box} onChange={(e) => setForm((f) => ({ ...f, subscription: { ...f.subscription, honeys_per_box: e.target.value } }))} className="w-full rounded-xl border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-bark">Tarif annuel — mensuelle (€)</label>
                        <input data-testid="pf-sub-monthly" type="number" min="0" step="0.01" value={form.subscription.monthly_price} onChange={(e) => setForm((f) => ({ ...f, subscription: { ...f.subscription, monthly_price: e.target.value } }))} placeholder="390,00" className="w-full rounded-xl border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-bark">Tarif annuel — bimestrielle (€)</label>
                        <input data-testid="pf-sub-bimonthly" type="number" min="0" step="0.01" value={form.subscription.bimonthly_price} onChange={(e) => setForm((f) => ({ ...f, subscription: { ...f.subscription, bimonthly_price: e.target.value } }))} placeholder="210,00" className="w-full rounded-xl border border-amber-900/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      </div>
                    </div>
                    <p className="text-xs text-stone-mute">Laissez un tarif vide pour désactiver cette cadence.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <MediaLibraryPicker
            open={boxImgPickerIdx !== null}
            onClose={() => setBoxImgPickerIdx(null)}
            onSelect={(url) => { const i = boxImgPickerIdx; setForm((f) => ({ ...f, box_options: f.box_options.map((o, idx) => (idx === i ? { ...o, image_url: url } : o)) })); }}
            defaultTag="produits"
          />

          <div data-testid="pf-crosssell" className="space-y-4 rounded-xl border border-amber-900/10 bg-cream/60 p-4">
            <div>
              <p className="text-sm font-medium text-bark">Produits croisés</p>
              <p className="mb-2 text-xs text-stone-mute">Ces produits s'afficheront dans « Vous aimerez aussi » sur la fiche client.</p>
              <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-amber-900/10 bg-white p-2">
                {allProducts.filter((p) => p.product_id !== productId).length === 0 ? (
                  <p className="px-2 py-1 text-xs text-stone-mute">Aucun autre produit disponible.</p>
                ) : (
                  allProducts.filter((p) => p.product_id !== productId).map((p) => (
                    <label key={p.product_id} data-testid={`pf-related-product-${p.product_id}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-bark hover:bg-cream">
                      <input type="checkbox" checked={(form.related_products || []).includes(p.product_id)} onChange={() => toggleArrayItem("related_products", p.product_id)} className="h-4 w-4 accent-amber-deep" />
                      <span className="truncate">{p.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-bark">Catégories croisées</p>
              <p className="mb-2 text-xs text-stone-mute">Complète « Vous aimerez aussi » avec des produits de ces catégories.</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => {
                  const active = (form.related_categories || []).includes(c.category_id);
                  return (
                    <button
                      type="button"
                      key={c.category_id}
                      data-testid={`pf-related-category-${c.category_id}`}
                      onClick={() => toggleArrayItem("related_categories", c.category_id)}
                      className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${active ? "bg-amber-deep text-white" : "border border-amber-800/20 bg-white text-bark-soft hover:border-amber-deep"}`}
                    >
                      {c.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-bark">Anciennes URL de QR code (redirection)</p>
              <p className="mb-2 text-xs text-stone-mute">Collez l'ancien slug de vos étiquettes/QR codes (ex. « miel-chataignier-250-gr »). Les anciens QR codes seront automatiquement redirigés vers cette fiche. Séparez par une virgule si plusieurs.</p>
              <input
                data-testid="pf-slug-aliases"
                value={(form.slug_aliases || []).join(", ")}
                onChange={(e) => setForm((f) => ({ ...f, slug_aliases: e.target.value.split(/[,\n]/).map((s) => s.trim()).filter(Boolean) }))}
                placeholder="miel-chataignier-250-gr"
                className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep"
              />
            </div>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <p className="mb-3 text-sm font-medium text-bark">Photo principale</p>
            {form.image_url ? (
              <img data-testid="pf-image-preview" src={resolveImage(form.image_url)} alt="Aperçu" className="mb-3 aspect-square w-full rounded-xl border border-amber-900/10 object-cover" />
            ) : (
              <div className="mb-3 flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-amber-900/25 bg-cream text-stone-mute">
                <ImagePlus size={32} />
              </div>
            )}
            <label
              data-testid="pf-upload-label"
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-amber-700/30 px-4 py-2.5 text-sm font-medium text-amber-900 transition-colors hover:border-amber-deep hover:bg-amber-50 ${uploading ? "opacity-60" : ""}`}
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
              {uploading ? "Téléversement…" : form.image_url ? "Changer la photo" : "Téléverser une photo"}
              <input data-testid="pf-upload-input" type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
            </label>
            <button
              type="button"
              data-testid="pf-library-btn"
              onClick={() => setPickerOpen(true)}
              className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-amber-900/15 px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
            >
              <Images size={15} /> Choisir dans la bibliothèque
            </button>
            <p className="mt-2 text-xs text-stone-mute">JPG, PNG ou WebP — 5 Mo maximum.</p>
          </div>

          <ProductGalleryEditor
            images={form.images}
            mainUrl={form.image_url}
            onChange={(images) => setForm((f) => ({ ...f, images }))}
            onSetMain={(i) => setForm((f) => {
              const images = [...f.images];
              const [picked] = images.splice(i, 1, f.image_url);
              return { ...f, image_url: picked, images: images.filter(Boolean) };
            })}
          />

          <MediaLibraryPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(url) => setForm((f) => ({ ...f, image_url: url }))}
            defaultTag="produits"
          />

          {isEdit && (
            <div data-testid="pf-qr-section" className="space-y-3 rounded-2xl border border-amber-800/15 bg-cream/60 p-5">
              <div className="flex items-center gap-2 text-amber-deep">
                <QrCode size={18} />
                <p className="font-serif text-lg font-semibold text-bark">QR Code du produit</p>
              </div>
              <p className="text-xs text-bark-soft">
                Encode l'adresse publique de la fiche : <span className="break-all font-medium text-bark">{window.location.origin}/produit/{productId}</span>. Le client scanne et arrive directement sur ce produit.
              </p>
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
                ⚠️ En développement : à régénérer après la mise en production (l'adresse du site changera).
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <button type="button" data-testid="pf-qr-generate" onClick={generateQr} disabled={qrLoading}
                  className="flex items-center gap-2 rounded-full border border-amber-800/25 bg-white px-4 py-2.5 text-sm font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep disabled:opacity-60">
                  {qrLoading ? <Loader2 size={15} className="animate-spin" /> : <QrCode size={15} />} {qrUrl ? "Régénérer" : "Générer le QR Code"}
                </button>
                {qrUrl && (
                  <button type="button" data-testid="pf-qr-download" onClick={downloadQr}
                    className="flex items-center gap-2 rounded-full bg-amber-deep px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-honey">
                    <Download size={15} /> Télécharger (PNG)
                  </button>
                )}
              </div>
              {qrUrl && (
                <img data-testid="pf-qr-preview" src={qrUrl} alt="QR Code du produit" className="h-44 w-44 rounded-xl border border-amber-900/10 bg-white p-2" />
              )}
            </div>
          )}

          <button
            data-testid="pf-submit"
            type="submit"
            disabled={saving || uploading}
            className="w-full rounded-full bg-amber-deep py-4 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : isEdit ? "Enregistrer les modifications" : "Créer le produit"}
          </button>
        </div>
      </form>
    </div>
  );
}
